import { z } from 'zod'

import {
  formSchema,
  formBasicInfoSchema,
  formTemplateSchema,
  createFormRequestSchema,
  updateFormRequestSchema,
  createFormResponseSchema,
  createFormTemplateRequestSchema,
  formPublicLinkSchema,
  type Form,
  type FormBasicInfo,
  type FormTemplate,
  type CreateFormRequest,
  type UpdateFormRequest,
  type CreateFormResponse,
  type CreateFormTemplateRequest,
  type FormPublicLink,
  type CreatePublicLinkRequest,
} from '@/types/api/form'

import { apiClient } from './client'

export async function getCurrentForm(recordGroupId: string): Promise<Form> {
  const response = await apiClient.get(`/forms/record-group/${recordGroupId}`, {
    suppressErrorToast: true,
  })
  return formSchema.parse(response.data)
}

/** Returns only basic info (id, version, recordGroupId) — no fields. */
export async function getAllForms(recordGroupId: string): Promise<FormBasicInfo[]> {
  const response = await apiClient.get(`/forms/record-group/all/${recordGroupId}`)
  return z.array(formBasicInfoSchema).parse(response.data)
}

/** Returns full form with fields for a specific formId. */
export async function getForm(formId: string): Promise<Form> {
  const response = await apiClient.get(`/forms/${formId}`)
  return formSchema.parse(response.data)
}

export async function createForm(data: CreateFormRequest): Promise<CreateFormResponse> {
  const validated = createFormRequestSchema.parse(data)
  const response = await apiClient.post('/forms', validated)
  return createFormResponseSchema.parse(response.data)
}

export async function updateForm(data: UpdateFormRequest): Promise<CreateFormResponse> {
  const validated = updateFormRequestSchema.parse(data)
  const response = await apiClient.put('/forms', validated)
  return createFormResponseSchema.parse(response.data)
}

export async function getFormTemplates(): Promise<FormTemplate[]> {
  const response = await apiClient.get('/form-templates')
  return z.array(formTemplateSchema).parse(response.data)
}

export async function createFormTemplate(data: CreateFormTemplateRequest): Promise<FormTemplate> {
  const validated = createFormTemplateRequestSchema.parse(data)
  const response = await apiClient.post('/form-templates', validated)
  return formTemplateSchema.parse(response.data)
}

export async function deleteFormTemplate(id: string): Promise<void> {
  await apiClient.delete(`/form-templates/${id}`)
}

// ── Public link management (authenticated) ────────────────────────────────────

export async function createPublicLink(data: CreatePublicLinkRequest): Promise<FormPublicLink> {
  const response = await apiClient.post('/forms/public-links', data)
  return formPublicLinkSchema.parse(response.data)
}

export async function getPublicLinksForForm(formId: string): Promise<FormPublicLink[]> {
  const response = await apiClient.get(`/forms/public-links/form/${formId}`)
  return z.array(formPublicLinkSchema).parse(response.data)
}

/** Returns all public links for the record group that owns the given form. */
export async function getPublicLinksForRecordGroup(formId: string): Promise<FormPublicLink[]> {
  const response = await apiClient.get(`/forms/public-links/record-group/${formId}`)
  return z.array(formPublicLinkSchema).parse(response.data)
}

export async function updatePublicLink(
  linkId: string, data: Partial<CreatePublicLinkRequest>
): Promise<FormPublicLink> {
  const response = await apiClient.put(`/forms/public-links/${linkId}`, data)
  return formPublicLinkSchema.parse(response.data)
}

export async function togglePublicLink(linkId: string): Promise<FormPublicLink> {
  const response = await apiClient.patch(`/forms/public-links/${linkId}/toggle`)
  return formPublicLinkSchema.parse(response.data)
}

export async function deletePublicLink(linkId: string): Promise<void> {
  await apiClient.delete(`/forms/public-links/${linkId}`)
}
